/**
 * Organization / Creator / Project public video list ordering + library rows.
 *
 * Public payloads already return videos newest-first (created_at DESC).
 * Preserve that API order. Only apply a client sort when items carry explicit
 * timestamps (defensive for alternate payloads) — never scramble id-only lists.
 *
 * When video_subcategories are present, entity pages show subcategory cards
 * first (like /home) and only render direct videos that lack a subcategory.
 */

function timestampOf(video) {
  if (!video || typeof video !== "object") return null;
  return video.published_at || video.created_at || null;
}

/**
 * @param {Array} videos
 * @returns {Array} shallow copy in display order (newest → oldest)
 */
export function entityVideosNewestFirst(videos) {
  const list = Array.isArray(videos) ? videos.slice() : [];
  const hasTimestamps = list.some((v) => timestampOf(v) != null);
  if (!hasTimestamps) {
    return list;
  }
  list.sort((a, b) => {
    const aT = timestampOf(a) || 0;
    const bT = timestampOf(b) || 0;
    if (typeof aT === "number" && typeof bT === "number") {
      return bT - aT;
    }
    return String(bT).localeCompare(String(aT));
  });
  return list;
}

/**
 * @param {Array} subcategories
 * @returns {Array} newest-first by newest_video_at (API usually pre-sorted)
 */
export function entitySubcategoriesNewestFirst(subcategories) {
  const list = Array.isArray(subcategories) ? subcategories.slice() : [];
  const hasTs = list.some((s) => s && s.newest_video_at);
  if (!hasTs) {
    return list;
  }
  list.sort((a, b) => {
    const aT = (a && a.newest_video_at) || "";
    const bT = (b && b.newest_video_at) || "";
    return String(bT).localeCompare(String(aT));
  });
  return list;
}

/**
 * Build /home-style entity library sections from a public entity payload.
 *
 * @param {{ videos?: Array, video_subcategories?: Array }} entity
 * @returns {{ subcategoryCards: Array, directVideos: Array }}
 */
export function buildEntityLibrarySections(entity) {
  const payload = entity && typeof entity === "object" ? entity : {};
  const videos = entityVideosNewestFirst(payload.videos);
  const subcategories = entitySubcategoriesNewestFirst(
    payload.video_subcategories
  );

  if (!subcategories.length) {
    return {
      subcategoryCards: [],
      directVideos: videos,
    };
  }

  const covered = {};
  subcategories.forEach((sub) => {
    if (sub && sub.id != null) {
      covered[String(sub.id)] = true;
    }
  });

  const directVideos = videos.filter((video) => {
    const sid =
      video && video.sub_category_id != null
        ? String(video.sub_category_id)
        : video && video.subcategory && video.subcategory.id != null
          ? String(video.subcategory.id)
          : "";
    if (!sid) return true;
    return !covered[sid];
  });

  return {
    subcategoryCards: subcategories,
    directVideos,
  };
}

export default entityVideosNewestFirst;
